import React, { useState, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { translate } from '../../lib/translations';

interface DraftPick {
  id: number;
  description: string;
  originalUserName?: string;
  year?: number;
  round?: number;
}

interface TradeDraftPicksTableProps {
  data: DraftPick[];
  onAddToTrade: (pick: DraftPick) => void;
  language: string;
  theme: string;
  isUserTeam: boolean;
}

type SortConfig = {
  field: string;
  direction: 'asc' | 'desc';
};

export default function TradeDraftPicksTable({
  data,
  onAddToTrade,
  language,
  theme,
  isUserTeam
}: TradeDraftPicksTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'year', direction: 'asc' });
  const lang = language as 'fr' | 'en';

  const handleSort = (field: string) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown size={14} className="ms-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="ms-1" />
      : <ArrowDown size={14} className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      let aVal: any = a[sortConfig.field as keyof DraftPick];
      let bVal: any = b[sortConfig.field as keyof DraftPick];

      // Tri par description si les champs year/round ne sont pas disponibles
      if (sortConfig.field === 'year' && (!a.year || !b.year)) {
        aVal = a.description || '';
        bVal = b.description || '';
      }

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc'
        ? (aVal || 0) - (bVal || 0)
        : (bVal || 0) - (aVal || 0);
    });
    return sorted;
  }, [data, sortConfig]);

  if (data.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <p className="mb-0">
          {lang === 'fr' ? 'Aucun choix de draft disponible' : 'No draft picks available'}
        </p>
      </div>
    );
  }

  return (
    <div className="table-responsive trade-grid-container">
      <table className="table table-hover table-sm mb-0">
        <thead className={theme === 'dark' ? 'table-dark' : 'table-light'}>
          <tr>
            <th style={{ width: '60px', textAlign: 'center', padding: '0.3rem 0.4rem' }}>
            </th>
            <th style={{ minWidth: '180px', cursor: 'pointer', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('year')}>
              {translate('trade.draftpicks.table.column.description', lang)}
              {renderSortIcon('year')}
            </th>
            <th style={{ minWidth: '100px', padding: '0.3rem 0.4rem' }}>
              {translate('trade.draftpicks.table.column.original', lang)}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((pick) => (
            <tr key={pick.id}>
              <td className="text-center" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onAddToTrade(pick)}
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                >
                  +
                </button>
              </td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-info text-white" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}>
                    <span className="d-none d-md-inline">Choix de draft</span>
                    <span className="d-md-none">Draft</span>
                  </span>
                  <span className="fw-bold" style={{ fontSize: '0.8rem' }}>{pick.description}</span>
                </div>
              </td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                {pick.originalUserName ? (
                  <span className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-info'}`} style={{ fontSize: '0.8rem' }}>
                    {pick.originalUserName}
                  </span>
                ) : (
                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
